/** @jsxImportSource @emotion/react */
"use client";

import { type Interpolation } from "@emotion/react";
import { I18nMessage, useTranslations } from "@i18n/next";
import type { CommunityBroadcast } from "@packages/sdk";
import { getFullName, useDate } from "@packages/sdk";
import type { ThemeType } from "@packages/ui";
import {
  Avatar,
  colors,
  cssMediaQueryMinWidthTablet,
  radius,
  spacing,
  Text,
  TimeIcon,
} from "@packages/ui";
import type { HTMLAttributes } from "react";

import { formatBroadcastScheduleLabel } from "../../../lib/utils/hallowAdminDashboard/broadcasts";
import { BroadcastOptionsDropdownMenu } from "./BroadcastOptionsDropdownMenu";
import { SentToModal } from "./SentToModal";

const broadcastMessageStyles: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.ms,
};

const contentContainerStyles: Interpolation = {
  backgroundColor: colors.purple20,
  borderRadius: radius.l,
  color: colors.white,
  padding: spacing.xl,
  whiteSpace: "pre-line",
};

/**
 * The design's lavender treatment for a broadcast that has not gone out yet:
 * the sent bubble's accent colors swapped end for end. Flat brand colors rather
 * than theme tokens, matching the sent bubble above it.
 */
const contentContainerScheduledStyles: Interpolation = {
  backgroundColor: colors.purple99,
  color: colors.purple20,
};

const scheduledTagStyles: Interpolation = {
  alignItems: "center",
  backgroundColor: colors.purple99,
  borderRadius: radius.xs,
  color: colors.purple50,
  display: "flex",
  flexShrink: 0,
  justifyContent: "center",
  paddingBlock: spacing.xxs,
  paddingInline: spacing.s,
};

const scheduledTimeStyles: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: spacing.xxs,
};

const headerStyles: Interpolation = {
  display: "flex",
  gap: spacing.s,
  alignItems: "flex-start",
  [cssMediaQueryMinWidthTablet]: {
    alignItems: "center",
  },
};

const headerTextStyles: Interpolation = {
  display: "flex",
  flex: 1,
  flexDirection: "column",
  gap: spacing.t,
  minWidth: spacing.t,
};

const headerTextTimeStyles: Interpolation<ThemeType> = (theme: ThemeType) => ({
  color: theme.semanticColors.neutralsMedium,
});

type BroadcastMessageProps = Omit<
  HTMLAttributes<HTMLDivElement>,
  "children"
> & {
  data: CommunityBroadcast;
};

export const BroadcastMessage = ({ data, ...props }: BroadcastMessageProps) => {
  const { getDateFormatDifferenceNow } = useDate();
  const t = useTranslations();

  const isScheduled = Boolean(data.is_scheduled && data.scheduled_at);

  const scheduledDate = data.scheduled_at ? new Date(data.scheduled_at) : null;

  /**
   * Empty on the server: this renders in the reader's timezone, so formatting
   * it during SSR would not match what the client produces. Mirrors the guard
   * in `getDateFormatDifferenceNow`, which the sent state uses for the same
   * reason.
   */
  const scheduledLabel =
    typeof window === "undefined" || !scheduledDate
      ? ""
      : `${t("scheduled_content")} ${formatBroadcastScheduleLabel(
          scheduledDate,
          t,
        )}`;

  return (
    <div css={broadcastMessageStyles} {...props}>
      <div css={headerStyles}>
        <Avatar imgSrc={data.broadcaster_user?.image_url} size={40} />
        <div css={headerTextStyles}>
          <Text size="xl" type="detail">
            <strong>
              {data?.broadcaster_user
                ? getFullName({
                    firstName: data?.broadcaster_user?.name,
                    lastName: data?.broadcaster_user?.last_name,
                  })
                : t("direct_messages_deleted_user")}
            </strong>
            &nbsp;
            <I18nMessage message="dashboard_broadcast_sent_message" />
            &nbsp;
            <SentToModal
              communityId={data.broadcaster_community?.id}
              data={data}
            />
          </Text>
          {isScheduled ? (
            <div css={[headerTextTimeStyles, scheduledTimeStyles]}>
              <TimeIcon size={12} />
              <Text size="l" type="detail">
                {scheduledLabel}
              </Text>
            </div>
          ) : (
            <Text size="l" css={headerTextTimeStyles} type="detail">
              {getDateFormatDifferenceNow({ date: new Date(data.created_at) })}
            </Text>
          )}
        </div>
        {isScheduled && (
          <div css={scheduledTagStyles}>
            <Text size="l" type="detail">
              <I18nMessage message="scheduled_content" />
            </Text>
          </div>
        )}
        <BroadcastOptionsDropdownMenu
          broadcast={data}
          communityId={data.broadcaster_community?.id}
        />
      </div>
      <div
        css={[
          contentContainerStyles,
          isScheduled && contentContainerScheduledStyles,
        ]}
      >
        <Text as="p" size="l" type="body">
          {data.content}
        </Text>
      </div>
    </div>
  );
};

BroadcastMessage.displayName = "BroadcastMessage";
